import { MESSAGES_LIMIT } from "constants/messages";

import { formatValidQueryParamUrlCommaSeparated } from "../utils/common";
import { formatMessage } from "../utils/messages.util";
import { emptySplitApi } from "./empty-split-api";
import {
  AssigneeMessageInput,
  Message,
  MessageFilters,
  MessageInput,
  MessageResponse,
  ReadMessageInput,
  RecheckMessageDeliveryStatusInput,
  RecheckMessageDeliveryStatusResponse,
  RecheckMessageDeliveryStatusResult
} from "./types/messages.type";
import type { PromptOutputFormat } from "./types/prompts.type";

export const extendedMessagesApi = emptySplitApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getMessages: builder.query<Message[], MessageFilters>({
      query: ({
        patient,
        offset = 0,
        limit = MESSAGES_LIMIT,
        query = "{*}",
        ...params
      }) =>
        `/messages/?patient=${patient}&limit=${limit}&offset=${offset}&${formatValidQueryParamUrlCommaSeparated(
          params
        )}&ordering=-created_at,-id&query=${query}`,
      providesTags: ["Messages"],
      transformResponse: (response: MessageResponse[]) =>
        response.map(formatMessage)
    }),
    addMessage: builder.mutation<Message, MessageInput>({
      query: (messageData) => ({
        url: "/messages/",
        method: "POST",
        body: messageData
      }),
      invalidatesTags: ["Messages", "UnreadMessagesCount"]
    }),
    updateMessageStatus: builder.mutation<Message, ReadMessageInput>({
      query: ({ id, is_read }) => ({
        url: `/messages/${id}/`,
        method: "PATCH",
        body: { is_read }
      }),
      transformResponse: formatMessage,
      invalidatesTags: ["Messages", "UnreadMessagesCount", "PatientDialogs"]
    }),
    updateMessageAssignee: builder.mutation<Message, AssigneeMessageInput>({
      query: ({ id, assignee }) => ({
        url: `/messages/${id}/`,
        method: "PATCH",
        body: { assignee }
      }),
      transformResponse: formatMessage,
      invalidatesTags: ["Messages", "UnreadMessagesCount"]
    }),
    recheckMessageDeliveryStatus: builder.mutation<
      RecheckMessageDeliveryStatusResult,
      RecheckMessageDeliveryStatusInput
    >({
      query: ({ id }) => ({
        url: `/messages/${id}/recheck-delivery-status/`,
        method: "POST",
        timeout: 20000
      }),
      transformResponse: ({
        outcome,
        message
      }: RecheckMessageDeliveryStatusResponse) => ({
        outcome,
        message: formatMessage(message)
      })
      // Invalidates nothing on purpose. The caller patches the reconciled status into the thread's
      // own state, and a "Messages" refetch past page 0 returns rows usePagination already holds,
      // which it reads as the end of the list and stops infinite scroll for the session.
    }),
    autoCompleteMessage: builder.mutation<
      string,
      {
        message_id?: number;
        text?: string;
        clinic_id?: number;
        format?: PromptOutputFormat;
      }
    >({
      query: (messageData) => ({
        url: "/messages/complete",
        method: "POST",
        body: messageData
      })
    }),
    readAllPatientMessages: builder.mutation<void, number>({
      query: (patietId) => ({
        url: `/patients/${patietId}/messages/read/`,
        method: "PATCH"
      }),
      // "Messages" and "PatientDialogs" are deliberately NOT invalidated here:
      // useMessages' readPatientMessages/readAllMessages already refetch the
      // thread (refetchMessages) and invalidate PatientDialogs in .then().
      // Tag-invalidating them too double-fires every fetch on each
      // conversation switch — the SN-18999 inbox refetch storm.
      invalidatesTags: ["UnreadMessagesCount"]
    }),
    readAllMessagesGlobal: builder.mutation<void, void>({
      query: () => ({
        url: "/patients/messages/read_all/",
        method: "PATCH"
      }),
      invalidatesTags: ["UnreadMessagesCount"]
    }),
    getUnreadMessagesCount: builder.query<
      number,
      { clinicId?: number; clinics?: number[]; byPatientClinic?: boolean }
    >({
      // `byPatientClinic` is part of the cache key on purpose: flipping the flag must refetch
      // rather than reuse a count computed the other way.
      query: ({ clinicId, clinics, byPatientClinic }) =>
        `messages/unread-messages-count/?${formatValidQueryParamUrlCommaSeparated(
          {
            clinic: clinicId,
            clinics,
            scope: byPatientClinic ? "patient_clinic" : undefined
          }
        )}`,
      providesTags: ["UnreadMessagesCount"]
    })
  })
});

export const {
  useGetMessagesQuery,
  useAddMessageMutation,
  useUpdateMessageStatusMutation,
  useUpdateMessageAssigneeMutation,
  useRecheckMessageDeliveryStatusMutation,
  useAutoCompleteMessageMutation,
  useReadAllPatientMessagesMutation,
  useReadAllMessagesGlobalMutation,
  useGetUnreadMessagesCountQuery
} = extendedMessagesApi;
